/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-07-07 20:44:21
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Empty, Layout, Table, message, Tooltip } from 'antd'
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import vip_download_excel_icon from '../../../images/vip_download_excel.png'
import Filter from './Filter'
import dayjs from 'dayjs'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVip
} from '../../../app/hooks'
import {
  fetchVisitRecords,
  downloadVisitRecords
} from '../../../actions/customer'

import Viewer from '../../components/ViewerV2'
import type { ViewerRef } from '../../components/ViewerV2/types'
import Config from '../../../constants/config'
import DownloadHistory from './DownloadHistory'
import sensors, { sensorsRole } from '../../../utils/sensors'
import { fetchCustomersRules } from '../../../actions/group'
import {
  createDepartmentsColumns,
  createVisitScreenColumns,
  getImageUrl,
  getMediaType,
  parseFetchParams,
  renderVisitPhotos,
  shelfApprovalColumns,
  createPurchaseColumn
} from './lib'

import PageHeader from '@/components/PageHeader'
const Content = Layout.Content

export default () => {
  const dispatch = useAppDispatch()
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const groupId = useAppSelector((state) => state.group.groupId)
  const customerConfig = useAppSelector((state) => state.group.customerConfig)
  const isVip = useAppSelector((state) => state.group.isVip)
  const myRole = useAppSelector((state) => state.group.myRole)
  const viewerRef = useRef<ViewerRef>(null)

  const baseColumnsConfig = [
    {
      title: `${customerConfig.verb}时间`,
      dataIndex: 'visitTime',
      width: 126,
      key: 'visitTime'
    },
    {
      title: `${customerConfig.verb}人`,
      dataIndex: 'visitUserName',
      key: 'visitUserName',
      width: 98
    },
    {
      title: `${customerConfig.verb}总结`,
      dataIndex: 'visitSummary',
      width: 200,
      key: 'visitSummary'
    },
    {
      title: `${customerConfig.noun}名称`,
      dataIndex: 'customerName',
      key: 'customerName',
      width: 126
    },
    {
      title: `${customerConfig.noun}地址`,
      dataIndex: 'address',
      key: 'address',
      width: 160
    },
    {
      title: `${customerConfig.noun}ID`,
      dataIndex: 'customID',
      key: 'customID',
      width: 100
    },
    {
      title: '跟进人',
      dataIndex: 'owners',
      width: 200,
      render: (owners) => {
        return (
          <>
            {owners.map((o, i) => (
              <div key={i}>
                {o.userName} - {o.userMobile}
              </div>
            ))}
          </>
        )
      }
    },
    {
      title: '联系人',
      dataIndex: 'contractName',
      key: 'contractName',
      width: 126
    },
    {
      title: '联系电话',
      dataIndex: 'contractTel',
      key: 'contractTel',
      width: 130
    },
    {
      title: '停留时长（分钟）',
      dataIndex: 'visitDuration',
      key: 'visitDuration',
      width: 100
    },
    {
      title: '水印名称',
      dataIndex: 'watermarkNames',
      key: 'watermarkNames',
      width: 200,
      render: (_, record) => {
        return (
          <div>
            {record.watermarkNames?.map((name, index) => (
              <div key={index}>{name}</div>
            )) || '-'}
          </div>
        )
      }
    },
    {
      title: `${customerConfig.verb}照片`,
      dataIndex: 'visitPhotoUrls',
      key: 'visitPhotoUrls',
      width: 660,
      render: (visitPhotoUrls) => {
        return renderVisitPhotos({
          urls: visitPhotoUrls,
          onPreview: (photos, index) => {
            setPhotos(photos)
            viewerRef.current?.view(index)
          }
        })
      }
    },
    {
      title: () => {
        return (
          <div>
            <span>{customerConfig.verb}得分</span>
            <span style={{ marginLeft: '8px' }}>
              <Tooltip
                arrow={{ pointAtCenter: true }}
                placement="topLeft"
                title={() => {
                  return <div>拜访得分展示最新一次的打分结果</div>
                }}
              >
                <span>
                  <ExclamationCircleOutlined />
                </span>
              </Tooltip>
            </span>
          </div>
        )
      },
      dataIndex: 'score',
      key: 'score',
      width: 120,
      render: (text) => {
        return <div>{String(text) ? text : '-'}</div>
      }
    },
    {
      title: '打分人',
      dataIndex: 'scorerName',
      key: 'scorerName',
      width: 120,
      render: (text) => {
        return <div>{text ? text : '-'}</div>
      }
    }
  ]

  const defaultFilter: {
    times: [dayjs.Dayjs, dayjs.Dayjs]
    departmentUsers: { userID: string }[]
    departments: { id: string; num: number }[]
    watermarks: string[]
    approvalStatus: number
    approvalUsers: { userID: string }[]
    approvalDepartments: { id: string; num: number }[]
  } = {
    times: [dayjs().startOf('month'), dayjs()],
    departmentUsers: [],
    departments: [],
    watermarks: [],
    approvalStatus: -1,
    approvalUsers: [],
    approvalDepartments: []
  }

  const [filter, setFilter] = useState(defaultFilter)
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [columns, setColumns] = useState<any[]>(baseColumnsConfig)
  const [photos, setPhotos] = useState<any[]>([])
  const [downloadHistoryVisible, setDownloadHistoryVisible] = useState(false)
  const [isDownloadPhoto, setIsDownloadPhoto] = useState(false)
  const [isShowShelfVerification, setIsShowShelfVerification] = useState(false)
  const { benefits } = useVip()
  const payment = usePayment()

  const handleFetch = (filter) => {
    const departmentIDList = (filter.departments || []).map((department) =>
      Number(department.id)
    )

    const {
      userIDList,
      watermarks,
      approvalUserIDList,
      approvalDepartmentIDList
    } = parseFetchParams(filter)

    dispatch(
      fetchVisitRecords({
        userId,
        groupID: groupId,
        filter: {
          times: [
            {
              start: filter.times[0].format(Config.timeFormat),
              end: filter.times[1].format(Config.timeFormat)
            }
          ],
          userIDList,
          departmentIDList,
          watermarks,
          approvalStatus: filter.approvalStatus,
          approvalUserIDList,
          approvalDepartmentIDList
        }
      })
    )
      .then((res) => {
        const {
          labelGroups,
          list,
          isShowShelfVerification,
          isShowPurchase,
          photoScreenColumns,
          total
        } = res
        const labelColumns =
          labelGroups?.map((item) => {
            return {
              title: item.name,
              // dataIndex: item.ID,
              width: 98,
              key: item.ID,
              render: (_, record) => {
                return record.labelInfo[item.ID]
              }
            }
          }) || []

        const departmentsColumns = createDepartmentsColumns(list)

        let newColumns: any[] = [...baseColumnsConfig]
        if (isShowPurchase) {
          // 插入补货信息列， 在拜访总结和客户名称之间
          const visitSummaryColumnIndex = baseColumnsConfig.findIndex(
            (item) => item.dataIndex === 'visitSummary'
          )
          newColumns.splice(
            visitSummaryColumnIndex + 1,
            0,
            createPurchaseColumn()
          )
        }

        const ownerColumnIndex = baseColumnsConfig.findIndex(
          (item) => item.dataIndex === 'owners'
        )
        newColumns.splice(ownerColumnIndex + 1, 0, ...departmentsColumns)
        newColumns.splice(
          ownerColumnIndex + departmentsColumns.length + 1,
          0,
          ...labelColumns
        )

        if (isShowShelfVerification) {
          setIsShowShelfVerification(true)
          newColumns = newColumns.concat(shelfApprovalColumns)
        }

        if (photoScreenColumns && photoScreenColumns.length > 0) {
          const visitPhotoUrlsColumnIndex = newColumns.findIndex(
            (item) => item.dataIndex === 'visitPhotoUrls'
          )
          if (visitPhotoUrlsColumnIndex > -1) {
            const screenColumns = createVisitScreenColumns({
              photoScreenColumns,
              onPreview: (photos, index) => {
                setPhotos(photos)
                viewerRef.current?.view(index)
              }
            })
            newColumns.splice(visitPhotoUrlsColumnIndex, 1, ...screenColumns)
          }
        }

        setData(list || [])
        setTotal(total)
        setColumns(newColumns || [])
      })
      .catch(() => {
        setData([])
        setTotal(0)
        setColumns([])
      })
  }

  const reportSaClickEvent = (clickItem) => {
    sensors.track('web_workgroup_visit_record_click', {
      clickItem,
      role: sensorsRole(myRole),
      groupID: groupId,
      isGroupVIP: isVip
    })
  }

  const handleDownload = () => {
    reportSaClickEvent('downloadExcel')

    dispatch(
      fetchCustomersRules({
        groupID: groupId
      })
    ).then(
      ({ visitAggTypePurchaseCount, visitAggTypeUseCount, customerCount }) => {
        if (customerCount > 100 && visitAggTypePurchaseCount === 0) {
          return message.info(
            `请前往app购买「${customerConfig.noun}${customerConfig.verb}权益」`
          )
        } else if (
          // 195版本客户拜访可购买30人版，取消无限人数版本。 旧的无限人数版已购名额会返回int32最大值
          customerCount > 100 &&
          // visitAggTypePurchaseCount < 30 &&
          visitAggTypePurchaseCount < visitAggTypeUseCount
        ) {
          return message.info(
            `${customerConfig.noun}${customerConfig.verb}使用人数超出，请前往app进行调整`
          )
        }

        const departmentIDList = (filter.departments || []).map((department) =>
          String(department.id)
        )

        const {
          userIDList,
          watermarks,
          approvalUserIDList,
          approvalDepartmentIDList
        } = parseFetchParams(filter)

        dispatch(
          downloadVisitRecords({
            groupId,
            userId,
            startTime: filter.times[0].format(Config.timeFormat),
            endTime: filter.times[1].format(Config.timeFormat),
            showPhoto: isDownloadPhoto,
            userIDList,
            departmentIDList,
            watermarks,
            approvalStatus: filter.approvalStatus,
            approvalUserIDList,
            approvalDepartmentIDList
          })
        ).then(() => {
          setDownloadHistoryVisible(true)
        })
      }
    )
  }

  const handleSubmit = () => {
    handleFetch(filter)
  }

  const handleReset = () => {
    reportSaClickEvent('reset')
    setFilter(defaultFilter)
    handleFetch(defaultFilter)
  }

  useEffect(() => {
    handleFetch(filter)
  }, [])

  return (
    <Layout className="Customer">
      <PageHeader
        className="Header"
        ghost={true}
        title={`${customerConfig.noun}${customerConfig.verb}记录`}
        extra={
          <>
            <Checkbox
              checked={isDownloadPhoto}
              disabled={data.length === 0}
              onChange={(e) => {
                const { checked } = e.target

                reportSaClickEvent(
                  checked ? 'importExcelPhotos' : 'cancelImport'
                )

                setIsDownloadPhoto(checked)
              }}
            >
              导出Excel包含图片
            </Checkbox>
            <Button
              type="primary"
              onClick={handleDownload}
              disabled={data.length === 0}
            >
              下载表格Excel
              <img
                alt=""
                src={vip_download_excel_icon}
                style={{
                  width: 20,
                  height: 20,
                  verticalAlign: 'bottom'
                }}
              />
            </Button>
            <Button
              type="link"
              onClick={() => {
                reportSaClickEvent('downloadRecord')
                setDownloadHistoryVisible(true)
              }}
            >
              下载记录
            </Button>
          </>
        }
      />
      <Content className="content">
        <Filter
          filter={filter}
          setFilter={setFilter}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onReportSaClickEvent={reportSaClickEvent}
          isShowShelfVerification={isShowShelfVerification}
        />

        {data.length ? (
          <div className="table-wrapper">
            {total >= 200 && (
              <div className="tip">
                <InfoCircleOutlined
                  style={{
                    marginRight: 4
                  }}
                />
                仅展示前200条{customerConfig.verb}记录，下载后可查看全部
              </div>
            )}

            <Table
              bordered
              dataSource={data}
              columns={columns}
              pagination={false}
              rowKey={(record, index) => String(index)}
              scroll={{
                x: '100%',
                y: window.innerHeight - 400,
                scrollToFirstRowOnChange: true
              }}
            />
          </div>
        ) : (
          <Empty style={{ marginTop: '300px' }} />
        )}
      </Content>

      <Viewer
        ref={viewerRef}
        photos={photos}
        canFavorite={false}
        canDelete={false}
        canRectify={false}
        canAiSecurity={false}
        onFetchMore={() => {}}
        isHideWatermarkInfo={true}
      />

      <DownloadHistory
        visible={downloadHistoryVisible}
        onClose={() => setDownloadHistoryVisible(false)}
      />
    </Layout>
  )
}
